<template>
  <ClientOnly>
    <button
      :class="[
        isLiked
          ? 'text-blackly md:hover:text-blackly/90'
          : 'text-transparent hover:text-br-green hover:stroke-br-green',
        isLoading ? 'pointer-events-none' : '',
      ]"
      class="bg-putty p-4 rounded-full relative"
      @click.stop="like"
      :title="isLiked ? $t('exercises.removeLike') : $t('exercises.addLike')"
    >
      <span :class="{ invisible: isLoading }">
        <HeartIcon class="h-4" />
      </span>
      <div v-if="isLoading" class="absolute inset-0 flex items-center justify-center">
        <SpinnerIcon class="w-6 h-6 animate-spin text-br-green" />
      </div>
    </button>
  </ClientOnly>
</template>

<script setup>
import { ref, computed, defineComponent } from 'vue';
import HeartIcon from '@/components/icons/HeartIcon';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import { useRoutine } from '@/composables/useRoutine';
import { useBlackrollAuth } from '@/composables/useBlackrollAuth';
import { useUserData } from '@/composables/useUserData';
import { useLoginModalStore } from '@/stores/login-modal';

// Define component name
defineComponent({
  name: 'LikeBtn',
});

// Define props
const props = defineProps({
  type: {
    type: String,
    required: true,
  },
  handle: {
    type: String,
    required: true,
  },
  exercise: {
    type: Object,
    default: () => ({}),
  },
});

// Setup composables
const { $_gtm } = useNuxtApp();
const { entry, parseExerciseProperties } = useRoutine();
const { loggedIn } = useBlackrollAuth();
const { userDetails, addWorkout, deleteWorkout, addExercise, deleteExercise } = useUserData();
const loginModalStore = useLoginModalStore();

// Component state
const isLoading = ref(false);

// Computed values
const isLiked = computed(() => {
  if (!userDetails.value || !userDetails.value.blackrollCustomer) {
    return false;
  }

  if (props.type === 'routine' && userDetails.value.blackrollCustomer.workouts) {
    return (
      userDetails.value.blackrollCustomer.workouts.filter((elem) => elem.routineObjectId === props.handle).length > 0
    );
  } else if (props.type === 'exercise' && userDetails.value.blackrollCustomer.exercises) {
    return userDetails.value.blackrollCustomer.exercises.filter((elem) => elem.exerciseKey === props.handle).length > 0;
  }

  return false;
});

const pimId = computed(() => {
  return entry.value?.pimWorkout ? parseInt(entry.value.pimWorkout, 10) : null;
});

// Methods
const like = () => {
  if (loggedIn.value) {
    return isLiked.value ? removeLike() : addLike();
  } else {
    loginModalStore.showModal(() => addLike());
  }
};

const addLike = async () => {
  isLoading.value = true;

  try {
    if (props.type === 'routine') {
      $_gtm.push({
        event: 'gaEvent',
        event_name: 'routine_save',
        routine_save: {
          routine_name: props.exercise.title,
          sport: parseExerciseProperties(props.exercise.sports),
          training_goal: parseExerciseProperties(props.exercise.trainingGoals),
          difficulty: parseExerciseProperties(props.exercise.difficulty),
          routine_duration: props.exercise.duration,
          products: parseExerciseProperties(props.exercise.products),
          event_category: 'Routine',
          event_action: 'Save',
          event_label: props.exercise.title,
        },
      });

      // The addWorkout function now updates the userDetails directly
      await addWorkout(props.handle, pimId.value);
    } else if (props.type === 'exercise') {
      $_gtm.push({
        event: 'gaEvent',
        event_name: 'exercise_save',
        exercise_save: {
          exercise_name: props.exercise.title,
          product: parseExerciseProperties(props.exercise.products),
          training_goal: parseExerciseProperties(props.exercise.trainingGoals),
          event_category: 'Exercise',
          event_action: 'Save',
          event_label: props.exercise.title,
        },
      });

      // The addExercise function now updates the userDetails directly
      await addExercise(props.handle, props.exercise?.pimExerciseId);
    }
  } catch (error) {
    console.error(`Error adding ${props.type}:`, error);
  } finally {
    isLoading.value = false;
  }
};

const removeLike = async () => {
  isLoading.value = true;

  try {
    if (props.type === 'routine') {
      // The deleteWorkout function now updates the userDetails directly
      await deleteWorkout(props.handle, pimId.value);
    } else if (props.type === 'exercise') {
      // The deleteExercise function now updates the userDetails directly
      await deleteExercise(props.handle, props.exercise?.pimExerciseId);
    }
  } catch (error) {
    console.error(`Error removing ${props.type}:`, error);
  } finally {
    isLoading.value = false;
  }
};
</script>
